import React, { useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import FallbackLogo from '../FallbackLogo';

const ModernHeader = ({ 
  title, 
  subtitle, 
  onBackPress, 
  rightAction, 
  background = 'primary', 
  showLogo = false,
  sx = {},
  ...props 
}) => {
  const theme = useTheme();
  const [logoError, setLogoError] = useState(false);
  
  const getBackgroundStyles = () => {
    const primaryGradient = theme.customColors?.gradient?.primary || 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)';
    const secondaryGradient = theme.customColors?.gradient?.secondary || 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)';
    
    switch (background) {
      case 'primary':
        return {
          background: primaryGradient,
          color: theme.palette.primary.contrastText,
        };
      case 'secondary':
        return {
          background: secondaryGradient,
          color: theme.palette.secondary.contrastText,
        };
      case 'transparent':
        return {
          background: 'transparent',
          color: theme.palette.text.primary,
        };
      default:
        return {
          background: primaryGradient,
          color: theme.palette.primary.contrastText,
        };
    }
  };

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 10,
        ...getBackgroundStyles(),
        boxShadow: theme.customShadows?.card || '0 4px 20px rgba(0, 0, 0, 0.08)',
        backdropFilter: 'blur(10px)',
        borderBottom: `1px solid ${theme.palette.divider}20`,
        ...sx,
      }}
      {...props}
    >
      {/* Safe area for iOS notch */}
      <Box
        className="modern-header"
        sx={{
          paddingTop: 'env(safe-area-inset-top)',
          paddingX: { xs: 1.5, sm: 2 },
          paddingY: { xs: 1, sm: 1.5 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: { xs: 56, sm: 64 },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          {onBackPress && (
            <IconButton
              onClick={onBackPress}
              sx={{
                mr: 2,
                color: 'inherit',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: theme.customBorderRadius?.medium || '12px',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  transform: 'translateX(-2px)',
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              <ArrowBack />
            </IconButton>
          )}
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
            {showLogo && (
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {!logoError ? (
                  <Box
                    component="img"
                    src="/images/logo-simple.svg"
                    alt="MySocialBizCard Logo"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      filter: background === 'transparent' ? 'none' : 'brightness(0) invert(1)',
                    }}
                    onError={() => {
                      console.log('Logo failed to load, using fallback');
                      setLogoError(true);
                    }}
                    onLoad={() => console.log('Logo loaded successfully')}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '10px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '12px',
                        fontWeight: 700,
                        color: 'white',
                        letterSpacing: '0.5px',
                      }}
                    >
                      MSB
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: 'inherit',
                  fontSize: { xs: '1.1rem', sm: '1.25rem' },
                  lineHeight: 1.2,
                }}
              >
                {title}
              </Typography>
              {subtitle && (
                <Typography
                  variant="body2"
                  sx={{
                    color: 'inherit',
                    opacity: 0.8,
                    fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                    mt: 0.25,
                  }}
                >
                  {subtitle}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
        {rightAction && (
          <Box sx={{ ml: 2 }}>
            {rightAction}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ModernHeader;